/* hs-eslint ignored failing-rules */

'use es6';

import PropTypes from 'prop-types';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UIAlert from 'UIComponents/alert/UIAlert';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import partial from 'transmute/partial';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import H2 from 'UIComponents/elements/headings/H2';
import H4 from 'UIComponents/elements/headings/H4';
import { ReportActionsFloatingAlertStore } from 'reporting-action-components/shared/ReportActionsFloatingAlertStore';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getName, getId as getReportId } from '../../../data/schemas/reportSchema';
import { useHideDialog } from '../../../dialog/ducks';
import { useMutation } from 'data-fetching-client';
import { CHANGE_REPORT_OWNER } from '../../../data/requests/report';
import { getDefaultSavedReportActionEventProperties } from '../../report-action-wrapper';
import { useTracker } from '../../../utils/hooks/useTracker';
import { EVENTS } from '../../../utils/usageTracker';
import { useUserRoleForReport } from '../../../utils/trackingUtils';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Set as ImmutableSet } from 'immutable';
import { useState } from 'react';
import { useCreateAndOwnUsers } from '../../../data/hooks/useCreateAndOwnUsers';
import formatName from 'I18n/utils/formatName';
import { getFirstName, getLastName } from '../../../data/schemas/userSchema';
import useUserInfo from '../../../access/useUserInfo';
import { useHasReportingAdminAccess } from '../../../access/scopes/hooks/useHasScope';
import { getUserId } from '../../../data/schemas/userInfoSchema';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ChangeReportOwnerModal = ({
  appUsageTracker,
  reports,
  onChangeReportOwnerSuccess = noop,
  filteredReportCount = 0,
  filters
}) => {
  const usageTracker = useTracker(appUsageTracker);
  const hideDialog = useHideDialog();
  const [changeReportOwnerMutation] = useMutation(CHANGE_REPORT_OWNER);
  const firstReport = !reports.isEmpty() && reports.first();
  const isChangeOwnerAll = reports.isEmpty() && filteredReportCount;
  const count = isChangeOwnerAll ? filteredReportCount : reports.size;
  const isMultiChangeOwner = count > 1;
  const reportName = firstReport ? getName(firstReport) : null;
  const isAdmin = useHasReportingAdminAccess();
  const ownerId = firstReport ? String(firstReport.get('reportOwnerId')) : null;
  const [selectedId, setSelectedId] = useState(ownerId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isCurrentOwnerSelected = !isMultiChangeOwner && selectedId === ownerId;
  const reportIds = reports.reduce((memo, report) => memo.add(getReportId(report)), ImmutableSet()).toJS();
  const {
    data: userInfoData,
    loading: userInfoLoading
  } = useUserInfo();
  const currentUserId = !userInfoLoading && getUserId(userInfoData);
  const userRole = useUserRoleForReport(firstReport);
  const {
    data: createAndOwnUsersData,
    loading: createAndOwnUsersLoading
  } = useCreateAndOwnUsers();
  const getReportOwnerName = owner => {
    const reportOwner = createAndOwnUsersData.get('createAndOwnUsers').get(owner);
    if (reportOwner) {
      return formatName({
        firstName: getFirstName(reportOwner),
        lastName: getLastName(reportOwner)
      });
    }
    return null;
  };
  let ownerLabels;
  let displayedValue;
  let reportOwner;
  if (!createAndOwnUsersLoading) {
    ownerLabels = createAndOwnUsersData.get('createAndOwnUsersLabels').toJS();
    reportOwner = getReportOwnerName(ownerId);
    displayedValue = ownerLabels.findIndex(({
      value
    }) => value === selectedId) >= 0 ? selectedId : null;
  }
  const handleChange = ({
    target: {
      value
    }
  }) => {
    setSelectedId(value);
  };
  const handleConfirm = () => {
    if (usageTracker) {
      const baseChangeOwnerAssetProperties = {
        actionType: 'save',
        action: 'change-owner',
        object: 'owner',
        count,
        assetType: 'report'
      };
      const changeOwnerAssetProperties = isMultiChangeOwner ? Object.assign({}, baseChangeOwnerAssetProperties) : Object.assign({}, baseChangeOwnerAssetProperties, getDefaultSavedReportActionEventProperties(firstReport), {
        role: userRole
      });
      usageTracker.track(EVENTS.completeAssetUpdate, changeOwnerAssetProperties);
    }
    const updatedReport = firstReport ? firstReport.set('reportOwnerId', selectedId) : null;
    changeReportOwnerMutation({
      variables: {
        reportIds,
        ownerId: selectedId,
        filters
      }
    }).then(() => {
      ReportActionsFloatingAlertStore.addAlert({
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "reporting-action-components.actions.changeReportOwner.modal.success",
          options: {
            reportName,
            count,
            filters
          }
        }),
        type: 'success',
        'data-test-id': 'report-save-success-alert'
      });
      onChangeReportOwnerSuccess(updatedReport);
    }, () => ReportActionsFloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.changeReportOwner.modal.failure"
      }),
      type: 'danger'
    })).then(() => {
      hideDialog();
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  };
  const newReportOwner = !createAndOwnUsersLoading && getReportOwnerName(selectedId);
  const ConfirmModal = /*#__PURE__*/_jsxs(UIModal, {
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: hideDialog
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.changeReportOwner.modal.confirmMessage"
        })
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.changeReportOwner.modal.confirmDescription",
        options: {
          reportOwner,
          newReportOwner,
          reportName,
          count
        }
      })
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        use: "primary",
        onClick: handleConfirm,
        disabled: isCurrentOwnerSelected,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.changeReportOwner.modal.confirm"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        use: "secondary",
        onClick: hideDialog,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.changeReportOwner.modal.reject"
        })
      })]
    })]
  });
  const AssignToMeButton = /*#__PURE__*/_jsx(UIButton, {
    use: "link",
    onClick: partial(handleChange, SyntheticEvent(String(currentUserId))),
    "data-test-id": "assign-to-me-button",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.changeReportOwner.select.assignToMe"
    })
  });
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "report-change-owner-modal",
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: hideDialog
      }), /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.changeReportOwner.modal.message"
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [isMultiChangeOwner && /*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.selectedCount.report",
          options: {
            count
          }
        })
      }), /*#__PURE__*/_jsx(UIForm, {
        onSubmit: partial(setConfirmOpen, true),
        children: /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.changeReportOwner.modal.description"
          }),
          children: createAndOwnUsersLoading ? /*#__PURE__*/_jsx(UILoadingSpinner, {
            layout: "centered"
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(UISelect, {
              onChange: handleChange,
              options: ownerLabels,
              value: displayedValue,
              dropdownFooter: AssignToMeButton,
              placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "reporting-action-components.actions.changeReportOwner.select.placeholder"
              }),
              "data-test-id": "change-owner-select"
            }), isAdmin ? null : /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(UIAlert, {
                type: "warning",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "reporting-action-components.actions.changeReportOwner.modal.warningMessage"
                })
              })]
            })]
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        disabled: !isCurrentOwnerSelected,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.changeReportOwner.select.alreadyOwner"
        }),
        children: /*#__PURE__*/_jsx(UIButton, {
          use: "primary",
          onClick: () => {
            if (!isMultiChangeOwner) {
              handleConfirm();
            } else {
              setConfirmOpen(true);
            }
          },
          "data-test-id": "change-owner-button",
          disabled: isCurrentOwnerSelected,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "reporting-action-components.actions.changeReportOwner.modal.confirm"
          })
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        use: "secondary",
        onClick: hideDialog,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "reporting-action-components.actions.changeReportOwner.modal.reject"
        })
      }), confirmOpen && ConfirmModal]
    })]
  });
};
ChangeReportOwnerModal.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  reports: ImmutablePropTypes.set.isRequired,
  onChangeReportOwnerSuccess: PropTypes.func,
  filteredReportCount: PropTypes.number,
  filters: PropTypes.object
};
export default ChangeReportOwnerModal;